import { useTranslation } from "react-i18next";
import { Upload, Palette, Share2, Globe } from "lucide-react";
import uploadImage from "@/assets/upload-interface.jpg";
import aiImage from "@/assets/ai-processing.jpg";

const HowItWorksSection = () => {
  const { t } = useTranslation();

  const steps = [
    { icon: Upload, step: "01", title: t("howItWorks.step1Title"), description: t("howItWorks.step1Desc") },
    { icon: Palette, step: "02", title: t("howItWorks.step2Title"), description: t("howItWorks.step2Desc") },
    { icon: Share2, step: "03", title: t("howItWorks.step3Title"), description: t("howItWorks.step3Desc") },
    { icon: Globe, step: "04", title: t("howItWorks.step4Title"), description: t("howItWorks.step4Desc") },
  ];

  return (
    <section id="how-it-works" className="py-24 bg-hawk-navy text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-radial-orange opacity-30" />
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-hawk-orange/10 blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-hawk-orange/10 blur-3xl translate-x-1/2 translate-y-1/2" />
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 text-hawk-orange text-sm font-semibold mb-4">{t("howItWorks.badge")}</span>
          <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">{t("howItWorks.heading")}</h2>
          <p className="text-lg text-white/70">{t("howItWorks.subtitle")}</p>
        </div>
        <div className="max-w-6xl mx-auto mb-16">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
              <img src={uploadImage} alt="Photo upload interface" className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-hawk-navy via-hawk-navy/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-hawk-orange flex items-center justify-center"><Upload size={20} className="text-white" /></div>
                  <span className="font-display font-bold">{t("howItWorks.bulkUpload")}</span>
                </div>
                <p className="text-white/70 text-sm">{t("howItWorks.bulkUploadDesc")}</p>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
              <img src={aiImage} alt="AI processing visualization" className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-hawk-navy via-hawk-navy/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center"><Palette size={20} className="text-white" /></div>
                  <span className="font-display font-bold">{t("howItWorks.brandCustomise")}</span>
                </div>
                <p className="text-white/70 text-sm">{t("howItWorks.brandCustomiseDesc")}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
          {steps.map((step, index) => (
            <div key={step.step} className="relative group">
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-12 left-1/2 w-full h-0.5 bg-gradient-to-r from-hawk-orange/50 to-hawk-orange/20" />
              )}
              <div className="relative z-10 text-center">
                <div className="text-6xl font-display font-bold text-hawk-orange/20 mb-4">{step.step}</div>
                <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-hawk-orange to-hawk-gold flex items-center justify-center mb-6 shadow-glow group-hover:scale-110 transition-transform duration-300">
                  <step.icon size={36} className="text-white" />
                </div>
                <h3 className="text-xl font-display font-bold mb-3">{step.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
